import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { providersService } from '../services/providersService';
import { toast } from 'react-toastify';
import { UserRole, ProviderType, PricingModel, PaymentMode } from '../types';
import { RootState } from '../store/store';
import { DeleteConfirmationModal } from '../components/DeleteConfirmationModal';

interface ProviderFormData {
  name: string;
  providerType: ProviderType;
  pricingModel: PricingModel;
  paymentMode: PaymentMode;
  contactEmail?: string;
  contactPhone?: string;
  address?: string;
  notes?: string;
  isActive?: boolean;
}

const ProviderFormPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;

  const canManageProviders = user?.role === UserRole.MANAGER ||
                             user?.role === UserRole.ORG_ADMIN ||
                             user?.role === UserRole.PLATFORM_ADMIN;

  const [loading, setLoading] = useState(isEditMode);
  const [providerName, setProviderName] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [dependencies, setDependencies] = useState<{ canDelete: boolean; reasons: string[] } | undefined>();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ProviderFormData>({
    defaultValues: {
      providerType: ProviderType.COMPANY,
      pricingModel: PricingModel.MANUAL,
      paymentMode: PaymentMode.PER_TRANSFER,
      isActive: true,
    },
  });

  const providerType = watch('providerType');
  const isWhatsApp = providerType === ProviderType.WHATSAPP;

  // Auto-select Manual pricing when WhatsApp is selected
  useEffect(() => {
    if (isWhatsApp) {
      setValue('pricingModel', PricingModel.MANUAL);
    }
  }, [isWhatsApp, setValue]);

  useEffect(() => {
    const loadData = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const provider = await providersService.getById(id);
        setProviderName(provider.name);
        reset({
          name: provider.name,
          providerType: provider.providerType || ProviderType.COMPANY,
          pricingModel: provider.pricingModel || PricingModel.MANUAL,
          paymentMode: provider.paymentMode || PaymentMode.PER_TRANSFER,
          contactEmail: provider.contactEmail || '',
          contactPhone: provider.contactPhone || '',
          address: provider.address || '',
          notes: provider.notes || '',
          isActive: provider.isActive,
        });
      } catch (error: any) {
        console.error('Failed to load provider:', error);
        toast.error('Failed to load provider data');
        navigate('/app/providers');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id, navigate, reset]);

  const onSubmit = async (data: ProviderFormData) => {
    try {
      // Clean up empty strings
      // WhatsApp providers must always be Manual pricing
      const finalPricingModel = data.providerType === ProviderType.WHATSAPP
        ? PricingModel.MANUAL
        : data.pricingModel;

      const cleanedData = {
        ...data,
        providerType: data.providerType,
        pricingModel: finalPricingModel,
        paymentMode: data.paymentMode || PaymentMode.PER_TRANSFER,
        contactEmail: data.contactEmail || undefined,
        contactPhone: data.contactPhone || undefined,
        address: data.address || undefined,
        notes: data.notes || undefined,
      };

      if (isEditMode && id) {
        await providersService.update(id, cleanedData);
        toast.success('Provider updated successfully');
      } else {
        await providersService.create(cleanedData);
        toast.success('Provider created successfully');
      }

      navigate('/app/providers');
    } catch (error: any) {
      console.error('Failed to save provider:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        'Failed to save provider';
      toast.error(errorMessage);
    }
  };

  const handleDeleteClick = async () => {
    if (!id) return;

    try {
      const deps = await providersService.checkDependencies(id);
      setDependencies(deps);
    } catch (error) {
      setDependencies(undefined);
    }
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!id) return;

    await providersService.delete(id);
    toast.success(t('providers.deleted'));
    setIsDeleteModalOpen(false);
    navigate('/app/providers');
  };

  if (!canManageProviders) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to manage providers.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
      <div className="mb-4 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
          {isEditMode ? 'Edit Provider' : 'New Provider'}
        </h1>
        <p className="text-sm sm:text-base text-gray-600 mt-1">
          {isEditMode
            ? 'Update provider information'
            : 'Add a new external provider (Premium Transfer, VIP Transport, etc.)'}
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 sm:space-y-6">
        <div className="bg-white shadow sm:rounded-lg p-4 sm:p-6">
          <div className="space-y-4 sm:space-y-6">
            {/* Provider Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Provider Name *
              </label>
              <input
                type="text"
                {...register('name', { required: 'Name is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Premium Transfer, VIP Transport"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.name.message}
                </p>
              )}
            </div>

            {/* Provider Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Provider Type *
              </label>
              <select
                {...register('providerType', { required: 'Type is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={ProviderType.COMPANY}>Company (Provider fills driver info)</option>
                <option value={ProviderType.WHATSAPP}>WhatsApp Group (You fill driver info)</option>
              </select>
              <p className="mt-1 text-sm text-gray-500">
                {providerType === ProviderType.WHATSAPP
                  ? 'WhatsApp group: You will enter driver name, phone and plate when creating transfers'
                  : 'Company: Provider will fill driver information themselves'}
              </p>
            </div>

            {/* Pricing Model */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fiyatlandırma Modeli *
              </label>
              <select
                {...register('pricingModel', { required: 'Pricing model is required' })}
                disabled={isWhatsApp}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isWhatsApp
                    ? 'bg-gray-100 border-gray-300 text-gray-500 cursor-not-allowed'
                    : 'border-gray-300'
                }`}
              >
                <option value={PricingModel.MANUAL}>Manual (Serbest Giriş)</option>
                <option value={PricingModel.FIXED}>Fixed (Sözleşmeli / Sabit)</option>
              </select>
              <p className="mt-1 text-sm text-gray-500">
                {isWhatsApp
                  ? 'WhatsApp grupları için fiyatlandırma her zaman Manual olarak belirlenir'
                  : 'Manual: Her transferde fiyat serbestçe girilir. Fixed: Sabit sözleşmeli fiyatlar uygulanır.'}
              </p>
            </div>

            {/* Payment Mode */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ödeme Modu *
              </label>
              <select
                {...register('paymentMode', { required: 'Payment mode is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={PaymentMode.PER_TRANSFER}>Transfer Bazlı</option>
                <option value={PaymentMode.LUMP_SUM}>Toplu Ödeme (Cari)</option>
              </select>
              <p className="mt-1 text-sm text-gray-500">
                Transfer Bazlı: Her transfer için ayrı ödeme takibi. Toplu Ödeme: Cari hesap sistemi.
              </p>
            </div>

            {/* Contact Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact Email
              </label>
              <input
                type="email"
                {...register('contactEmail', {
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="info@provider.com"
              />
              {errors.contactEmail && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.contactEmail.message}
                </p>
              )}
            </div>

            {/* Contact Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact Phone
              </label>
              <input
                type="tel"
                {...register('contactPhone')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="+90 555 123 4567"
              />
            </div>

            {/* Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address
              </label>
              <textarea
                {...register('address')}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t('providers.addressPlaceholder')}
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                {...register('notes')}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t('providers.notesPlaceholder')}
              />
            </div>

            {/* Active Status (only show in edit mode) */}
            {isEditMode && (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  {...register('isActive')}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">
                  Active
                </label>
              </div>
            )}
          </div>
        </div>

        {/* Users Management Section (only in edit mode) */}
        {isEditMode && id && (
          <div className="bg-white dark:bg-gray-800 shadow sm:rounded-lg p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  {t('users.title')}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Bu tedarikçi için kullanıcı hesapları yönetin. İlk eklenen kullanıcı otomatik olarak Tedarikçi Yöneticisi olur.
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate(`/app/providers/${id}/users`)}
                className="px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 active:bg-green-800 transition-colors min-h-[44px] font-medium flex items-center gap-2"
              >
                <span>Kullanıcıları Yönet</span>
                <span>→</span>
              </button>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col-reverse sm:flex-row justify-between gap-3">
          {isEditMode ? (
            <button
              type="button"
              onClick={handleDeleteClick}
              className="px-4 py-2.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors min-h-[44px] font-medium"
            >
              {t('common.delete')}
            </button>
          ) : (
            <div />
          )}
          <div className="flex flex-col-reverse sm:flex-row gap-3">
            <button
              type="button"
              onClick={() => navigate('/app/providers')}
              className="px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 active:bg-gray-100 transition-colors min-h-[44px] font-medium"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors disabled:bg-blue-400 min-h-[44px] font-medium"
            >
              {isSubmitting
                ? t('common.saving')
                : isEditMode
                ? t('common.update')
                : t('common.create')}
            </button>
          </div>
        </div>
      </form>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setDependencies(undefined);
        }}
        onConfirm={handleDeleteConfirm}
        entityName={providerName}
        entityType="provider"
        requirePassword={true}
        dependencies={dependencies}
      />
    </div>
  );
};

export default ProviderFormPage;
